import PlusIcon from "@redotech/redo-icons/plus.svg";
import type { Store } from "@redotech/redo-partners-portal-rpc/schema/shared/store";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import {
  RedoTable,
  type RedoTableColumn,
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableCellLink } from "@redotech/redo-web/arbiter-components/tables/redo-table-cell-link";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import {
  usePageSubtitle,
  usePageTitleActions,
} from "@redotech/redo-web/page-header-slot";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CreateStoreModal } from "./create-store-modal";
import { useStores } from "./use-stores";

function storePath(storeId: string): string {
  return `/stores/${encodeURIComponent(storeId)}`;
}

export const StoreOriginBadge = memo(function StoreOriginBadge({
  origin,
}: {
  origin: Store["origin"];
}) {
  return (
    <RedoBadge
      color="gray"
      size="sm"
      text={origin === "created" ? "Created" : "Connected"}
    />
  );
});

const columns: RedoTableColumn<Store>[] = [
  {
    key: "name",
    renderHeaderCell: () => <RedoTableTextCell text="Store" />,
    renderNormalCell: ({ row }) => (
      <RedoTablePaddedCell>
        <RedoTableCellLink to={storePath(row.id)}>
          <Text fontSize="sm" fontWeight="medium" m="none">
            {row.name}
          </Text>
        </RedoTableCellLink>
      </RedoTablePaddedCell>
    ),
  },
  {
    key: "url",
    renderHeaderCell: () => <RedoTableTextCell text="URL" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={row.storeUrl ?? "—"} />
    ),
  },
  {
    key: "origin",
    renderHeaderCell: () => <RedoTableTextCell text="Access" />,
    renderNormalCell: ({ row }) => (
      <RedoTablePaddedCell>
        <StoreOriginBadge origin={row.origin} />
      </RedoTablePaddedCell>
    ),
  },
  {
    key: "added",
    renderHeaderCell: () => <RedoTableTextCell text="Added" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={new Date(row.createdAt).toLocaleDateString()} />
    ),
  },
];

export const StoresPage = memo(function StoresPage() {
  const stores = useStores();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  usePageSubtitle("The Redo stores your integration manages.");
  usePageTitleActions(
    useMemo(
      () => (
        <RedoButton
          hierarchy="primary"
          IconLeading={PlusIcon}
          onClick={() => setCreating(true)}
          size="sm"
          text="Create store"
        />
      ),
      [],
    ),
  );

  return (
    <>
      {stores.isError ? (
        <RedoEmptyState
          primaryButtonProps={{
            text: "Try again",
            onClick: () => void stores.refetch(),
          }}
          subtext="Something went wrong loading your stores."
          text="Couldn't load stores"
        />
      ) : (
        <RedoTable
          emptyStateContent={
            stores.isPending ? (
              <RedoTableLoadingStateContent />
            ) : (
              <RedoTableEmptyStateContent text="No stores yet" />
            )
          }
          layout={{ columns, standalone: true }}
          onRowClick={(row) => navigate(storePath(row.id))}
          rowsOfData={stores.data ?? []}
        />
      )}
      {creating && (
        <CreateStoreModal
          onClose={() => setCreating(false)}
          onCreated={(store) => navigate(storePath(store.id))}
        />
      )}
    </>
  );
});
